// src/pages/Admin/views/verification-rules/BannedPhonesPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  listBannedPhones,
  batchUpdateBannedPhones
} from './verificationRulesApi'; // Ensure this path is correct
import CrudTable from '../../../../components/Admin/VerificationRules/CrudTable'; // Adjust path to your CrudTable
import { formatDate, formatDateTime } from '../../../../utility/dateFormatter'; // Adjust path
import { Button } from '../../../../components/ui/button'; // Adjust path
import { useToast } from '../../../../hooks/use-toast'; // Using shadcn/ui useToast from your specified path
import { validatePhone } from '../../../../utility/contactValidation';

const BannedPhonesPage = () => {
  const [allBannedPhones, setAllBannedPhones] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  // const [successMessage, setSuccessMessage] = useState(''); // Using toast for success
  const { toast } = useToast();

  const [stagedChanges, setStagedChanges] = useState([]);

  const [isAdding, setIsAdding] = useState(false);
  const [newItemData, setNewItemData] = useState({
    phone_number: '', // Changed
    reason: '',
    is_fatal: true,
    is_active: true,
    expires_at: '',
  });

  const [editingItemId, setEditingItemId] = useState(null);
  const [currentItemDataForEdit, setCurrentItemDataForEdit] = useState(null);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    // setSuccessMessage('');
    try {
      const response = await listBannedPhones(); // Changed
      setAllBannedPhones(response.data || []);
      setStagedChanges([]);
    } catch (err) {
      console.error("Fetch Error Raw:", err.response || err);
      let errorMessage = "Failed to fetch banned phones."; // Changed
      if (err.response && err.response.data && err.response.data.detail) {
        errorMessage = typeof err.response.data.detail === 'string' ? err.response.data.detail : JSON.stringify(err.response.data.detail);
      } else if (err.message) {
        errorMessage = `Failed to fetch banned phones: ${err.message}`; // Changed
      }
      setError(errorMessage);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: errorMessage,
      })
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (editingItemId && !allBannedPhones.find(item => (item.id || item.tempId) === editingItemId)) {
      setEditingItemId(null);
      setCurrentItemDataForEdit(null);
    }
  }, [allBannedPhones, editingItemId]);

  const stageChange = (itemDataWithChange) => {
    setStagedChanges(prevChanges => {
      const keyOfChangedItem = itemDataWithChange.id || itemDataWithChange.tempId;
      let itemExistsInStaged = false;
      const updatedChanges = prevChanges.map(existingChange => {
        const keyOfExistingChange = existingChange.id || existingChange.tempId;
        if (keyOfExistingChange === keyOfChangedItem) {
          itemExistsInStaged = true;
          return { ...existingChange, ...itemDataWithChange };
        }
        return existingChange;
      });
      if (!itemExistsInStaged) {
        return [...updatedChanges, { ...itemDataWithChange }];
      }
      return updatedChanges;
    });
  };

  const handleEditClick = (item) => {
    const currentItemState = allBannedPhones.find(i => (i.id || i.tempId) === (item.id || item.tempId)) || item;
    if (editingItemId === (currentItemState.id || currentItemState.tempId)) {
      handleCancelEdit();
    } else {
      setEditingItemId(currentItemState.id || currentItemState.tempId);
      const dataForEditing = {
        ...currentItemState,
        expires_at: currentItemState.expires_at ? formatDate(currentItemState.expires_at) : '',
      };
      const stagedVersion = stagedChanges.find(sc => (sc.id || sc.tempId) === (currentItemState.id || currentItemState.tempId));
      if (stagedVersion) {
        setCurrentItemDataForEdit({
          ...dataForEditing,
          ...stagedVersion,
          expires_at: stagedVersion.expires_at ? formatDate(stagedVersion.expires_at) : '',
        });
      } else {
        setCurrentItemDataForEdit(dataForEditing);
      }
    }
    // setSuccessMessage('');
    setError(null);
  };

  const handleCancelEdit = () => {
    setEditingItemId(null);
    setCurrentItemDataForEdit(null);
  };

  const handleInputChange = (e) => {
    // setSuccessMessage('');
    const { name, value, type, checked } = e.target;
    const L_value = type === 'checkbox' ? checked : value;
    if (editingItemId) {
      setCurrentItemDataForEdit(prev => ({ ...prev, [name]: L_value }));
    } else if (isAdding) {
      setNewItemData(prev => ({ ...prev, [name]: L_value }));
    }
  };

  const applyRowEditChanges = () => {
    if (!editingItemId || !currentItemDataForEdit) return;

    const phoneNumberInput = currentItemDataForEdit.phone_number.trim();

    if (!phoneNumberInput) {
        setError("Phone number cannot be empty when editing.");
        toast({
          variant: 'destructive',
          title: 'Error',
          description: "Phone number cannot be empty when editing.",
        })
        return; // Or handle as appropriate
    }
    if (!validatePhone(phoneNumberInput)) {
      setError("Edited phone number must be 10 digits.");
      toast({
        variant: 'destructive',
        title: 'Error',
        description: "Edited phone number must be 10 digits.",
      })
      return;
    }
    setError(null);


    const editedData = { ...currentItemDataForEdit, phone_number: phoneNumberInput };
    editedData.isNew = !!(editedData.tempId && !editedData.id);
    if (editedData.id && typeof editedData.id === 'number') {
      delete editedData.tempId;
    }
    setAllBannedPhones(prevAll =>
      prevAll.map(phone =>
        (phone.id === editingItemId || phone.tempId === editingItemId)
          ? { ...phone, ...editedData }
          : phone
      )
    );
    stageChange(editedData);
    toast({
      variant: 'success',
      title: 'Changes Applied',
      description: 'Changes for the row applied. Click "Save All Changes" to persist.',
    });
  }
 

  const handleAddNewRowToList = () => {
    const phoneNumberInput = newItemData.phone_number.trim(); // Trim whitespace

    if (!phoneNumberInput) {
      setError("Phone number is required.");
      toast({
        title: 'Error',
        description: "Phone number is required.",
        variant: 'destructive',
      })
      return;
    }
    // Validate for 10 digits using your utility
    if (!validatePhone(phoneNumberInput)) {
      setError("Please enter a valid 10-digit phone number.");
      toast({
        title: 'Error',
        description: "Please enter a valid 10-digit phone number.",
        variant: 'destructive',
      })
      return;
    }
    setError(null);

    const tempId = `temp-${Date.now()}`;
    const newPhoneEntry = { // Changed
      ...newItemData,
      tempId: tempId,
      isNew: true,
      expires_at: newItemData.expires_at || null,
    };
    delete newPhoneEntry.id;

    setAllBannedPhones(prev => [newPhoneEntry, ...prev]); // Changed
    stageChange(newPhoneEntry);

    setNewItemData({ phone_number: '', reason: '', is_fatal: true, is_active: true, expires_at: '' }); // Changed
    setIsAdding(false);
    toast({
      title: "Success",
      description: "Phone number added to list. Click 'Save All Changes' to persist.",
      variant: "success"
    })
  };

  const handleDeleteOrDeactivate = (itemToToggle) => {
    const itemCurrentState = allBannedPhones.find(i => (i.id || i.tempId) === (itemToToggle.id || itemToToggle.tempId)) || itemToToggle; // Changed
    if (window.confirm(`Are you sure you want to ${itemCurrentState.is_active ? 'deactivate' : 'activate'} "${itemCurrentState.phone_number || 'this item'}"? This will be saved on "Save All".`)) { // Changed
      // setSuccessMessage('');
      setError(null);
      const newActiveState = !itemCurrentState.is_active;
      setAllBannedPhones(prevAllPhones => // Changed
        prevAllPhones.map(phone => { // Changed
          const phoneKey = phone.id || phone.tempId; // Changed
          const itemToToggleKey = itemCurrentState.id || itemCurrentState.tempId;
          if (phoneKey === itemToToggleKey) {
            return { ...phone, is_active: newActiveState };
          }
          return phone;
        })
      );
      const changeToStage = {
        ...itemCurrentState,
        is_active: newActiveState,
        isNew: !itemCurrentState.id,
        expires_at: itemCurrentState.expires_at ? formatDate(itemCurrentState.expires_at) : null,
      };
      if (changeToStage.isNew) delete changeToStage.id;
      else delete changeToStage.tempId;
      stageChange(changeToStage);
      if (editingItemId === (itemCurrentState.id || itemCurrentState.tempId)) {
        setCurrentItemDataForEdit(prev => ({ ...prev, is_active: newActiveState }));
      }
    }
  };

  const handleSaveAll = async () => {
    if (editingItemId && currentItemDataForEdit) {
      applyRowEditChanges();
      setEditingItemId(null);
      setCurrentItemDataForEdit(null);
    }
    if (stagedChanges.length === 0) {
      return;
    }
    setIsLoading(true);
    setError(null);
    // setSuccessMessage('');
    const payload = stagedChanges.map(change => {
      const {
        isNew,
        tempId,
        _isDeleted,
        created_at,
        updated_at,
        id,
        phone_number, // Pull out phone_number specifically
        ...itemSpecificData
      } = change;

      const backendItem = { ...itemSpecificData };
      backendItem.expires_at = backendItem.expires_at ? formatDate(backendItem.expires_at) : null;

      // Phone number transformation logic
      let finalPhoneNumber = phone_number.trim();
      if (validatePhone(finalPhoneNumber)) { // If it's a 10-digit number
        finalPhoneNumber = `+91${finalPhoneNumber}`;
      }
      // If it's already in international format or some other format you accept,
      // you might add more sophisticated logic here or rely on backend validation.
      // For now, we only prepend +91 if it passes the 10-digit validation.
      backendItem.phone_number = finalPhoneNumber;


      if (isNew || !id || typeof id !== 'number') {
        // No 'id' for new items
      } else {
        backendItem.id = id;
      }
      return backendItem;
    });

    try {
      // console.log("Sending Banned Phones to batch update:", JSON.stringify(payload, null, 2));
      const response = await batchUpdateBannedPhones(payload); // Changed
      toast({
        variant: 'success',
        title: 'Success',
        description:`Batch update successful! ${response.data?.length || 0} phone numbers processed.`
      })
      setStagedChanges([]);
      fetchData();
    } catch (err) {
      console.error("Batch Update Error (Phones):", err.response?.data || err.message); // Changed
      let errorMessage = "An unexpected error occurred during batch update.";
      if (err.response && err.response.data) { /* ... (error parsing logic from BannedEmailsPage) ... */ 
        const errorData = err.response.data;
        if (errorData.detail) {
          if (typeof errorData.detail === 'string') errorMessage = errorData.detail;
          else if (Array.isArray(errorData.detail)) errorMessage = errorData.detail.map(e => `${e.loc ? e.loc.join(' -> ') + ': ' : ''}${e.msg}`).join('\n');
          else if (typeof errorData.detail === 'object') errorMessage = JSON.stringify(errorData.detail);
        } else if (typeof errorData === 'object' && Object.keys(errorData).length > 0) errorMessage = errorData.message || JSON.stringify(errorData);
        else if (typeof errorData === 'string') errorMessage = errorData;
      } else if (err.message) {
        errorMessage = `Batch update failed: ${err.message}`;
      }
      setError(errorMessage);
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    } finally {
      setIsLoading(false);
    }
  };

  const columns = [ // Adapted for Phone Numbers
    {
      key: 'phone_number', header: 'Phone Number', // Changed
      render: (item) => editingItemId === (item.id || item.tempId) ?
        <input type="tel" name="phone_number" value={currentItemDataForEdit?.phone_number || ''} onChange={handleInputChange} className="p-2 border border-gray-300 rounded-md w-full focus:ring-blue-500 focus:border-blue-500 text-base"/> : // Changed type to "tel"
        <span className="text-sm">{item.phone_number}</span> // Changed
    },
    {
      key: 'reason', header: 'Reason',
      render: (item) => editingItemId === (item.id || item.tempId) ?
        <input type="text" name="reason" value={currentItemDataForEdit?.reason || ''} onChange={handleInputChange} className="p-2 border border-gray-300 rounded-md w-full focus:ring-blue-500 focus:border-blue-500 text-base"/> :
        <span className="text-sm">{item.reason || '-'}</span>
    },
    {
      key: 'is_fatal', header: 'Fatal?',
      render: (item) => editingItemId === (item.id || item.tempId) ?
        <input type="checkbox" name="is_fatal" checked={!!currentItemDataForEdit?.is_fatal} onChange={handleInputChange} className="h-5 w-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"/> :
        <span className="text-sm">{item.is_fatal ? 'Yes' : 'No'}</span>
    },
    {
      key: 'is_active', header: 'Status',
      render: (item) => editingItemId === (item.id || item.tempId) ?
        ( <label className="flex items-center text-sm">
            <input type="checkbox" name="is_active" checked={!!currentItemDataForEdit?.is_active} onChange={handleInputChange} className="h-5 w-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mr-2"/>
            {currentItemDataForEdit?.is_active ? "Active" : "Inactive"}
          </label>
        ) :
        <span className={`text-sm font-semibold px-2 py-1 rounded-full ${item.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{item.is_active ? 'Active' : 'Inactive'}</span>
    },
    {
      key: 'expires_at', header: 'Expires At',
      render: (item) => editingItemId === (item.id || item.tempId) ?
        <input type="date" name="expires_at" value={currentItemDataForEdit?.expires_at || ''} onChange={handleInputChange} className="p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-base"/> :
        <span className="text-sm">{item.expires_at ? formatDate(item.expires_at) : 'N/A'}</span>
    },
    { key: 'created_at', header: 'Created', render: (item) => <span className="text-sm">{item.isNew && !item.id ? <span className="italic text-gray-400">New (Unsaved)</span> : formatDateTime(item.created_at)}</span> },
    { key: 'updated_at', header: 'Updated', render: (item) => <span className="text-sm">{item.isNew && !item.id ? <span className="italic text-gray-400">N/A</span> : formatDateTime(item.updated_at)}</span> },
  ];

  const activePhones = allBannedPhones.filter(phone => phone.is_active === true); // Changed
  const inactivePhones = allBannedPhones.filter(phone => phone.is_active === false); // Changed
  const showSaveAllButton = stagedChanges.length > 0 || editingItemId !== null;

  return (
    <div className="p-4 md:p-6">
      <h2 className="text-2xl font-semibold text-gray-800 mb-6 pb-2 border-b border-gray-200">
        Manage Banned Phone Numbers {/* Changed */}
      </h2>

      {isLoading && <p className="text-blue-600 animate-pulse">Loading data...</p>}
      {error && ( <div className="mb-4 p-3 bg-red-100 text-red-700 border border-red-300 rounded whitespace-pre-line"> {typeof error === 'string' ? error : JSON.stringify(error, null, 2)} </div> )}
      {/* Success messages handled by toast */}

      {!isAdding && (
        <Button onClick={() => { setIsAdding(true); setError(null); /*setSuccessMessage('');*/ handleCancelEdit(); }} variant="primary" size="md" className="mb-6">
          Add New Phone Number {/* Changed */}
        </Button>
      )}

      {isAdding && (
        <div className="mb-6 p-4 border border-gray-200 rounded-lg bg-gray-50 shadow">
          <h4 className="text-lg font-medium text-gray-700 mb-3">Add New Banned Phone Number</h4> {/* Changed */}
           <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
            <div className="flex flex-col">
              <label htmlFor="new_phone_number" className="mb-1 text-sm font-medium text-gray-600">Phone Number*</label> {/* Changed */}
              <input id="new_phone_number" type="tel" name="phone_number" placeholder="e.g., +14155552671" value={newItemData.phone_number} onChange={handleInputChange} required className="p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"/> {/* Changed */}
            </div>
            <div className="flex flex-col">
              <label htmlFor="new_phone_reason" className="mb-1 text-sm font-medium text-gray-600">Reason</label> {/* Changed id for uniqueness */}
              <input id="new_phone_reason" type="text" name="reason" placeholder="Reason for ban" value={newItemData.reason} onChange={handleInputChange} className="p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"/>
            </div>
            <div className="flex flex-col">
              <label htmlFor="new_phone_expires_at" className="mb-1 text-sm font-medium text-gray-600">Expires At</label> {/* Changed id */}
              <input id="new_phone_expires_at" type="date" name="expires_at" value={newItemData.expires_at} onChange={handleInputChange} className="p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"/>
            </div>
             <div className="flex items-center space-x-6 mt-2 md:mt-0 md:pt-6">
              <label className="flex items-center text-sm font-medium text-gray-600">
                <input type="checkbox" name="is_fatal" checked={newItemData.is_fatal} onChange={handleInputChange} className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mr-2"/> Is Fatal
              </label>
              <label className="flex items-center text-sm font-medium text-gray-600">
                <input type="checkbox" name="is_active" checked={newItemData.is_active} onChange={handleInputChange} className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mr-2"/> Is Active
              </label>
            </div>
          </div>
          <div className="mt-4 flex gap-x-3">
            <Button onClick={handleAddNewRowToList} variant="green" size="md">Add to List & Stage</Button>
            <Button onClick={() => { setIsAdding(false); setError(null); }} variant="outline" size="md">Cancel</Button>
          </div>
        </div>
      )}

      {showSaveAllButton && (
        <div className="my-6 text-right">
          <Button onClick={handleSaveAll} variant="primary" size="lg" isLoading={isLoading} disabled={isLoading || (editingItemId && !currentItemDataForEdit?.phone_number)}> {/* Changed */}
            {isLoading ? 'Saving All...' : `Save All (${stagedChanges.length}) Changes`}
          </Button>
        </div>
      )}

      <section className="mb-8">
        <h3 className="text-xl font-semibold text-gray-700 mb-3">Active Banned Phone Numbers ({activePhones.length})</h3> {/* Changed */}
        {activePhones.length > 0 ? (
            <CrudTable items={activePhones} columns={columns} onDeleteItem={handleDeleteOrDeactivate} editingItemId={editingItemId} onEditItem={handleEditClick} onCancelEdit={handleCancelEdit} onApplyEdit={applyRowEditChanges} /* ... */ />
        ) : (<p className="text-gray-500">No active banned phone numbers.</p>)} {/* Changed */}
      </section>

      <section>
        <h3 className="text-xl font-semibold text-gray-700 mb-3">Inactive Banned Phone Numbers ({inactivePhones.length})</h3> {/* Changed */}
         {inactivePhones.length > 0 ? (
            <CrudTable items={inactivePhones} columns={columns} onDeleteItem={handleDeleteOrDeactivate} editingItemId={editingItemId} onEditItem={handleEditClick} onCancelEdit={handleCancelEdit} onApplyEdit={applyRowEditChanges} /* ... */ />
         ) : (<p className="text-gray-500">No inactive banned phone numbers.</p>)} {/* Changed */}
      </section>
    </div>
  );
};

export default BannedPhonesPage;